import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { selectUnshownSuspendedOrders } from "../../../store/order-slice";
import { dismissSuspendedOrder } from "../../../store/order-actions";
import { CnCTransactionResponse } from "../../../types/api";
import styles from "./SuspendedOrdersNotification.module.scss";
import moment from "moment";

interface SuspendedOrderToastProps {
  order: CnCTransactionResponse;
  onDismiss: (transactionId: number) => void;
  isLoading: boolean;
}

const SuspendedOrderToast: React.FC<SuspendedOrderToastProps> = ({
  order,
  onDismiss,
  isLoading,
}) => {
  const orderTime = moment(order.orderCreated || order.created).format("HH:mm");
  const reason = "Nažalost, rezervacija je automatski preusmerena na drugu apoteku jer zahtev korisnika nije obrađen u roku od 10 minuta.";

  return (
    <div className={styles.Toast} role="alert">
      <div className={styles.ToastIcon}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>

      <div className={styles.ToastContent}>
        <div className={styles.ToastTitle}>Propuštena rezervacija</div>
        <div className={styles.ToastDetails}>
          <span className={styles.OrderNumber}>
            Porudžbina #{order.orderNumber}
          </span>
          <span className={styles.OrderTime}>{orderTime}</span>
        </div>
        <div className={styles.ToastReason}>{reason}</div>
      </div>

      <button
        className={styles.DismissButton}
        onClick={() => onDismiss(order.id)}
        disabled={isLoading}
        aria-label="Odbaci obaveštenje"
      >
        {isLoading ? (
          <span className={styles.Spinner} />
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
      </button>
    </div>
  );
};

const SuspendedOrdersNotification: React.FC = () => {
  const dispatch = useDispatch();
  const suspendedOrders = useSelector(selectUnshownSuspendedOrders);
  const isDismissing = useSelector(
    (state: RootState) => state.order.dismissingSuspendedOrder
  );

  const [dismissingId, setDismissingId] = useState<number | null>(null);

  const handleDismiss = async (transactionId: number) => {
    setDismissingId(transactionId);
    await dispatch<any>(dismissSuspendedOrder(transactionId));
    setDismissingId(null);
  };

  const handleDismissAll = async () => {
    for (const order of suspendedOrders) {
      await handleDismiss(order.id);
    }
  };

  // Don't render if no suspended orders
  if (suspendedOrders.length === 0) {
    return null;
  }

  return (
    <div className={styles.NotificationContainer}>
      {suspendedOrders.length > 1 && (
        <div className={styles.Header}>
          <span className={styles.HeaderTitle}>
            Propuštenih rezervacija: {suspendedOrders.length}
          </span>
          <button
            className={styles.DismissAllButton}
            onClick={handleDismissAll}
            disabled={isDismissing}
          >
            Odbaci sve
          </button>
        </div>
      )}

      <div className={styles.ToastStack}>
        {suspendedOrders.map((order) => (
          <SuspendedOrderToast
            key={`suspended-${order.id}`}
            order={order}
            onDismiss={handleDismiss}
            isLoading={dismissingId === order.id}
          />
        ))}
      </div>
    </div>
  );
};

export default SuspendedOrdersNotification;
